import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import GoogleIcon from "@mui/icons-material/Google";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import { useAuth, getAuthErrorMessage } from "../../firebase/authContext";
import { setReadingStorageUser } from "../../bible/readingStorageScope";
import { syncCloudReadingRecords } from "../../firebase/readingSyncService";
import { syncLocalNotesToCloud } from "../../firebase/notesService";

const AuthModal = ({ open, onClose, initialTab = 0 }) => {
  const [tabIndex, setTabIndex] = useState(initialTab);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const {
    currentUser,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signInAsGuest,
    resetPassword,
  } = useAuth();

  useEffect(() => {
    if (!open) {
      setErrorMsg("");
      setSuccessMsg("");
      setFieldErrors({ email: "", password: "" });
      setLoading(false);
    }
  }, [open, currentUser?.uid]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    setErrorMsg("");
    setSuccessMsg("");
    setFieldErrors({ email: "", password: "" });
  };

  const handlePostLoginSync = async (user) => {
    if (!user?.uid) return;
    try {
      setReadingStorageUser(user.uid);
      await syncCloudReadingRecords(user.uid);
      await syncLocalNotesToCloud(user);
    } catch (e) {
      console.warn("Post-login sync note:", e);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    const nextFieldErrors = {
      email: email ? "" : "请输入邮箱地址",
      password: tabIndex !== 2 && !password ? "请输入密码" : "",
    };
    setFieldErrors(nextFieldErrors);
    if (nextFieldErrors.email || nextFieldErrors.password) {
      return;
    }

    setLoading(true);
    try {
      if (tabIndex === 0) {
        // Sign In
        const user = await signInWithEmail(email, password);
        await handlePostLoginSync(user);
        onClose();
      } else if (tabIndex === 1) {
        // Sign Up
        if (password.length < 6) {
          setErrorMsg("密码至少需要 6 个字符");
          setLoading(false);
          return;
        }
        const user = await signUpWithEmail(email, password, displayName || "");
        await handlePostLoginSync(user);
        onClose();
      } else if (tabIndex === 2) {
        // Forgot Password
        await resetPassword(email);
        setSuccessMsg("密码重置邮件已发送至您的邮箱，请查收并按照指示重置密码。");
      }
    } catch (err) {
      setErrorMsg(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setErrorMsg("");
    setLoading(true);
    let settled = false;
    let leftWindow = false;
    const markClosed = () => {
      if (settled) return;
      setLoading(false);
      setErrorMsg((prev) => prev || "登录弹窗已关闭");
    };
    const onBlur = () => {
      leftWindow = true;
    };
    const onFocus = () => {
      if (!leftWindow) return;
      window.setTimeout(markClosed, 200);
    };
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    try {
      const user = await signInWithGoogle();
      settled = true;
      setErrorMsg("");
      await handlePostLoginSync(user);
      onClose();
    } catch (err) {
      settled = true;
      setErrorMsg(getAuthErrorMessage(err));
    } finally {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      setLoading(false);
    }
  };

  const isConfigError =
    errorMsg.includes("Firebase 控制台") ||
    errorMsg.includes("configuration-not-found") ||
    errorMsg.includes("未开启");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: { xs: 1, sm: 2 },
          bgcolor: (theme) => theme.palette.background.paper,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pt: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          {tabIndex === 0 && <LockOutlinedIcon color="primary" fontSize="small" />}
          {tabIndex === 1 && <PersonAddOutlinedIcon color="primary" fontSize="small" />}
          {tabIndex === 2 && <VpnKeyOutlinedIcon color="primary" fontSize="small" />}
          {tabIndex === 0 ? "读者登录" : tabIndex === 1 ? "注册新账号" : "找回密码"}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="关闭">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogTitle sx={{ p: 1, pb: 0 }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="登录" sx={{ fontWeight: 600, fontSize: "0.95rem" }} />
          <Tab label="注册" sx={{ fontWeight: 600, fontSize: "0.95rem" }} />
          <Tab label="找回密码" sx={{ fontWeight: 600, fontSize: "0.95rem" }} />
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Box
          data-testid="auth-alert-slot"
          sx={{
            minHeight: 56,
            mb: 1,
            display: "flex",
            alignItems: "flex-start",
          }}
        >
          {errorMsg ? (
            <Alert
              severity="error"
              sx={{ width: "100%", borderRadius: 2 }}
              onClose={() => setErrorMsg("")}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {errorMsg}
              </Typography>
              {isConfigError && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
                    配置解决步骤：进入 Firebase 控制台 → Authentication → 点击「开始使用 (Get started)」→ 在「Sign-in method」标签中启用 Email/Password 或 Google。
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      href="https://console.firebase.google.com/project/bible-aicoder/authentication"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.2 }}
                    >
                      前往 Firebase 控制台开启
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        await signInAsGuest();
                        onClose();
                      }}
                      sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.2 }}
                    >
                      以本地访客身份先体验
                    </Button>
                  </Box>
                </Box>
              )}
            </Alert>
          ) : successMsg ? (
            <Alert severity="success" sx={{ width: "100%", borderRadius: 2 }}>
              {successMsg}
            </Alert>
          ) : null}
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          {tabIndex === 1 && (
            <TextField
              id="auth-display-name"
              name="displayName"
              margin="normal"
              fullWidth
              label="昵称 / 主内称呼 (选填)"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="例如：约拿单"
              size="small"
              autoComplete="nickname"
            />
          )}

          <TextField
            id="auth-email"
            name="email"
            margin="normal"
            required
            fullWidth
            label="电子邮箱"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (fieldErrors.email) {
                setFieldErrors((prev) => ({ ...prev, email: "" }));
              }
            }}
            placeholder="name@example.com"
            autoComplete="email"
            autoFocus
            size="small"
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email || " "}
            FormHelperTextProps={{ sx: { minHeight: 20 } }}
          />

          {tabIndex !== 2 && (
            <TextField
              id="auth-password"
              name="password"
              margin="normal"
              required
              fullWidth
              label="密码"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (fieldErrors.password) {
                  setFieldErrors((prev) => ({ ...prev, password: "" }));
                }
              }}
              placeholder="至少 6 位密码"
              autoComplete={tabIndex === 0 ? "current-password" : "new-password"}
              size="small"
              error={Boolean(fieldErrors.password)}
              helperText={fieldErrors.password || " "}
              FormHelperTextProps={{ sx: { minHeight: 20 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="切换密码可见状态"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 2.5,
              mb: 1.5,
              py: 1,
              fontWeight: 700,
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : tabIndex === 0 ? (
              "登 录"
            ) : tabIndex === 1 ? (
              "立即注册"
            ) : (
              "发送重置邮件"
            )}
          </Button>

          {tabIndex !== 2 && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", my: 2 }}>
                <Divider sx={{ flexGrow: 1 }} />
                <Typography variant="caption" sx={{ px: 2, color: "text.secondary" }}>
                  或使用第三方登录
                </Typography>
                <Divider sx={{ flexGrow: 1 }} />
              </Box>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                disabled={loading}
                aria-label="使用 Google 账号登录"
                name="google-signin"
                sx={{
                  py: 0.9,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "divider",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                使用 Google 账号登录
              </Button>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "center", flexDirection: "column", gap: 1 }}>
        {tabIndex === 0 ? (
          <Typography variant="body2" color="text.secondary">
            还没有账号？{" "}
            <Button
              variant="text"
              size="small"
              onClick={() => handleTabChange(null, 1)}
              sx={{ fontWeight: 700, p: 0, minWidth: "auto" }}
            >
              点击注册
            </Button>
            {" · "}
            <Button
              variant="text"
              size="small"
              onClick={() => handleTabChange(null, 2)}
              sx={{ color: "text.secondary", p: 0, minWidth: "auto" }}
            >
              忘记密码
            </Button>
          </Typography>
        ) : tabIndex === 1 ? (
          <Typography variant="body2" color="text.secondary">
            已有账号？{" "}
            <Button
              variant="text"
              size="small"
              onClick={() => handleTabChange(null, 0)}
              sx={{ fontWeight: 700, p: 0, minWidth: "auto" }}
            >
              直接登录
            </Button>
          </Typography>
        ) : (
          <Button
            variant="text"
            size="small"
            onClick={() => handleTabChange(null, 0)}
            sx={{ fontWeight: 700 }}
          >
            返回登录
          </Button>
        )}

        <Button
          variant="text"
          size="small"
          onClick={async () => {
            await signInAsGuest();
            onClose();
          }}
          sx={{
            color: "text.secondary",
            fontSize: "0.78rem",
            textTransform: "none",
            "&:hover": { color: "primary.main" },
          }}
        >
          暂不登录，以本地离线模式体验全部功能 →
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthModal;
